import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card } from '../ui/card';
import type { Climb } from '../../types/climb';

type StatsGroupBy = 'setter' | 'grade' | 'zone';

interface GymStatsCardProps {
  climbs: Climb[];
  groupBy?: StatsGroupBy;
}

const LINE_COLORS = {
  climbCount: '#3b82f6',        // blue
  topsCount: '#22c55e',         // green
  avgDays: '#f97316',           // orange
  avgRating: '#ef4444',         // red
  avgGradeOpinion: '#a855f7',   // purple
  uniqueUsersTopped: '#14b8a6', // teal
};

type ViewMode = 'both' | 'chart' | 'table';

type ChartLineKey = 'climbCount' | 'topsCount' | 'avgDays';

export default function GymStatsCard({ climbs, groupBy = 'setter' }: GymStatsCardProps) {
  const { t } = useTranslation();
  const [hiddenLines, setHiddenLines] = useState<Set<ChartLineKey>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>('both');

  const handleLegendClick = (dataKey: string) => {
    const key = dataKey as ChartLineKey;
    setHiddenLines(prev => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      return next;
    });
  };

  // Summary stats
  const summaryStats = useMemo(() => {
    if (climbs.length === 0) return null;

    const ratingsWithValues = climbs.filter(c => c.average_rating != null);
    const avgRating = ratingsWithValues.length > 0
      ? ratingsWithValues.reduce((sum, c) => sum + (c.average_rating ?? 0), 0) / ratingsWithValues.length
      : null;

    const now = Date.now();
    const newestClimb = climbs.reduce((newest, c) =>
      new Date(c.created_at).getTime() > new Date(newest.created_at).getTime() ? c : newest
    , climbs[0]);
    const daysSinceNewest = Math.floor((now - new Date(newestClimb.created_at).getTime()) / (1000 * 60 * 60 * 24));

    return { avgRating, climbCount: climbs.length, daysSinceNewest };
  }, [climbs]);

  // Chart data grouped by setter or grade
  const chartData = useMemo(() => {
    if (climbs.length === 0) return [];

    const now = Date.now();

    type GroupEntry = {
      name: string;
      sortKey: number;
      climbCount: number;
      topsCount: number;
      uniqueUserIds: Set<number>;
      totalDays: number;
      ratingSum: number;
      ratingCount: number;
      gradeOpinionSum: number;
      gradeOpinionCount: number;
    };

    const groups = new Map<string, GroupEntry>();

    for (const climb of climbs) {
      let key: string;
      let sortKey: number;

      if (groupBy === 'setter') {
        key = climb.route_setter?.name || t('gymStats.noSetter');
        sortKey = 0;
      } else if (groupBy === 'zone') {
        key = climb.zone?.name || t('gymStats.noZone');
        sortKey = 0;
      } else {
        key = climb.difficulty || t('gymStats.noGrade');
        sortKey = climb.difficulty_value ?? Infinity;
      }

      if (!groups.has(key)) {
        groups.set(key, {
          name: key,
          sortKey,
          climbCount: 0,
          topsCount: 0,
          uniqueUserIds: new Set(),
          totalDays: 0,
          ratingSum: 0,
          ratingCount: 0,
          gradeOpinionSum: 0,
          gradeOpinionCount: 0,
        });
      }

      const entry = groups.get(key)!;
      entry.climbCount += 1;

      const sends = climb.sends ?? [];
      entry.topsCount += sends.length;
      sends.forEach(s => entry.uniqueUserIds.add(s.attributes.user.id));

      const daysAgo = Math.floor((now - new Date(climb.created_at).getTime()) / (1000 * 60 * 60 * 24));
      entry.totalDays += daysAgo;

      if (climb.average_rating != null) {
        entry.ratingSum += climb.average_rating;
        entry.ratingCount += 1;
      }
      if (climb.average_grade_opinion != null) {
        entry.gradeOpinionSum += climb.average_grade_opinion;
        entry.gradeOpinionCount += 1;
      }
    }

    const sorted = [...groups.values()].sort((a, b) =>
      groupBy === 'grade' ? a.sortKey - b.sortKey : a.name.localeCompare(b.name)
    );

    return sorted.map(entry => ({
      name: entry.name,
      climbCount: entry.climbCount,
      topsCount: entry.topsCount,
      uniqueUsersTopped: entry.uniqueUserIds.size,
      avgDays: entry.climbCount > 0 ? Math.round(entry.totalDays / entry.climbCount) : 0,
      avgRating: entry.ratingCount > 0 ? parseFloat((entry.ratingSum / entry.ratingCount).toFixed(2)) : null,
      avgGradeOpinion: entry.gradeOpinionCount > 0 ? parseFloat((entry.gradeOpinionSum / entry.gradeOpinionCount).toFixed(2)) : null,
    }));
  }, [climbs, groupBy, t]);

  if (!summaryStats) return null;

  const showContent = chartData.length >= 2;
  const rotateXLabels = chartData.length > 6;

  const tableRows: Array<{ labelKey: string; dataKey: keyof typeof chartData[0]; colorKey: keyof typeof LINE_COLORS; format: (v: number | null) => string }> = [
    { labelKey: 'chartClimbCount', dataKey: 'climbCount', colorKey: 'climbCount', format: v => v?.toString() ?? '–' },
    { labelKey: 'chartTopsCount', dataKey: 'topsCount', colorKey: 'topsCount', format: v => v?.toString() ?? '–' },
    { labelKey: 'chartUniqueUsers', dataKey: 'uniqueUsersTopped', colorKey: 'uniqueUsersTopped', format: v => v?.toString() ?? '–' },
    { labelKey: 'chartAvgDays', dataKey: 'avgDays', colorKey: 'avgDays', format: v => v?.toString() ?? '–' },
    { labelKey: 'chartAvgRating', dataKey: 'avgRating', colorKey: 'avgRating', format: v => v != null ? v.toFixed(2) : '–' },
    { labelKey: 'chartAvgGradeOpinion', dataKey: 'avgGradeOpinion', colorKey: 'avgGradeOpinion', format: v => v != null ? v.toFixed(2) : '–' },
  ];

  return (
    <Card className="overflow-hidden">
      <AccordionPrimitive.Root type="single" collapsible>
        <AccordionPrimitive.Item value="stats" className="border-0">
          {/* Trigger = summary stats row */}
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center group">
              <div className="grid grid-cols-3 gap-4 text-center flex-1 px-5 pt-5 pb-4">
                <div>
                  <p className="text-2xl font-bold">
                    {summaryStats.avgRating != null ? summaryStats.avgRating.toFixed(1) : '–'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('gymStats.avgRating')}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{summaryStats.climbCount}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('gymStats.climbCount')}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{summaryStats.daysSinceNewest}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('gymStats.daysSinceNewest')}</p>
                </div>
              </div>
              <ChevronDown className="h-4 w-4 shrink-0 mr-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>

          <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            {/* View toggle */}
            <div className="flex items-center justify-end px-4 pb-3 pt-1 border-t gap-3">
              <div className="flex bg-muted rounded-lg p-0.5 text-xs">
                {(['chart', 'table', 'both'] as ViewMode[]).map(mode => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`px-2.5 py-1 rounded-md transition-colors ${
                      viewMode === mode
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t(`gymStats.view_${mode}`)}
                  </button>
                ))}
              </div>
            </div>

            {!showContent ? (
              <p className="text-sm text-muted-foreground text-center py-4 px-4">{t('gymStats.noData')}</p>
            ) : (
              <>
                {/* Chart */}
                {(viewMode === 'chart' || viewMode === 'both') && (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData} margin={{ top: 5, right: 45, left: 5, bottom: rotateXLabels ? 60 : 20 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 11 }}
                        angle={rotateXLabels ? -45 : 0}
                        textAnchor={rotateXLabels ? 'end' : 'middle'}
                        interval={0}
                      />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={40} />
                      <Tooltip />
                      <Legend
                        onClick={(e) => handleLegendClick(e.dataKey as string)}
                        wrapperStyle={{ cursor: 'pointer', fontSize: 11 }}
                      />
                      <Line yAxisId="left" type="monotone" dataKey="climbCount" name={t('gymStats.chartClimbCount')} stroke={LINE_COLORS.climbCount} strokeWidth={2} dot={false} hide={hiddenLines.has('climbCount')} connectNulls />
                      <Line yAxisId="left" type="monotone" dataKey="topsCount" name={t('gymStats.chartTopsCount')} stroke={LINE_COLORS.topsCount} strokeWidth={2} dot={false} hide={hiddenLines.has('topsCount')} connectNulls />
                      <Line yAxisId="left" type="monotone" dataKey="avgDays" name={t('gymStats.chartAvgDays')} stroke={LINE_COLORS.avgDays} strokeWidth={2} dot={false} hide={hiddenLines.has('avgDays')} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                )}

                {/* Table */}
                {(viewMode === 'table' || viewMode === 'both') && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="border-t bg-muted/50">
                          <th className="text-left font-medium text-muted-foreground px-3 py-2 whitespace-nowrap sticky left-0 bg-muted/50 z-10">
                            —
                          </th>
                          {chartData.map(row => (
                            <th key={row.name} className="text-center font-medium px-3 py-2 whitespace-nowrap border-l">
                              {row.name}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tableRows.map(({ labelKey, dataKey, colorKey, format }, i) => (
                          <tr key={labelKey} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                            <td className={`px-3 py-1.5 font-medium text-muted-foreground whitespace-nowrap sticky left-0 z-10 ${i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}>
                              <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: LINE_COLORS[colorKey] }} />
                              {t(`gymStats.${labelKey}`)}
                            </td>
                            {chartData.map(row => (
                              <td key={row.name} className="text-center px-3 py-1.5 tabular-nums border-l">
                                {format(row[dataKey] as number | null)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </AccordionPrimitive.Content>
        </AccordionPrimitive.Item>
      </AccordionPrimitive.Root>
    </Card>
  );
}
